import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { ValidateStatus } from 'antd/lib/form/FormItem';
import { colorsPalette } from '../../styles/colors-palette';
import { ValidationContext } from './Validation';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle, FontSize } from '@tiptap/extension-text-style';
import Image from '@tiptap/extension-image';
import UiKit from '../index';

interface CustomStyles {
  minHeight?: CSSSize;
}

interface TextEditorProps {
  onChange: (value: PlainText) => void;
  value: PlainText;
  className?: string;
  customStyles?: CustomStyles;
}

const FONT_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'];

const EXTENSIONS = [
  StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
  Underline,
  Link.configure({ openOnClick: false }),
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  TextStyle,
  FontSize,
  Image.configure({ inline: true, allowBase64: true }),
];

function getActiveHeading(editor: ReturnType<typeof useEditor>): string {
  if (!editor) return 'paragraph';
  if (editor.isActive('heading', { level: 1 })) return 'h1';
  if (editor.isActive('heading', { level: 2 })) return 'h2';
  if (editor.isActive('heading', { level: 3 })) return 'h3';
  return 'paragraph';
}

function getActiveFontSize(editor: ReturnType<typeof useEditor>): string {
  if (!editor) return '';
  return (editor.getAttributes('textStyle').fontSize as string) || '';
}

function LinkInput({
  onSubmit,
  onCancel,
}: {
  onSubmit: (url: string) => void;
  onCancel: () => void;
}) {
  const [url, setUrl] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (url.trim()) onSubmit(url.trim());
    }
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <LinkInputWrapper>
      <LinkInputField
        value={url}
        onChange={e => setUrl(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Введите URL"
        autoFocus={true}
      />
      <ToolbarButton
        onClick={() => {
          if (url.trim()) onSubmit(url.trim());
        }}
        title="Применить"
      >
        <UiKit.icons.Check />
      </ToolbarButton>
      <ToolbarButton onClick={onCancel} title="Отмена">
        <UiKit.icons.Close />
      </ToolbarButton>
    </LinkInputWrapper>
  );
}

function EditorToolbar({ editor }: { editor: ReturnType<typeof useEditor> }) {
  const [showLinkInput, setShowLinkInput] = useState(false);

  if (!editor) return null;

  const handleHeadingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === 'paragraph') {
      editor.chain().focus().setParagraph().run();
    } else {
      const level = parseInt(value.replace('h', '')) as 1 | 2 | 3;
      editor.chain().focus().toggleHeading({ level }).run();
    }
  };

  const handleFontSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value) {
      editor.chain().focus().setFontSize(value).run();
    } else {
      editor.chain().focus().unsetFontSize().run();
    }
  };

  const insertImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        editor
          .chain()
          .focus()
          .setImage({ src: reader.result as string })
          .run();
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const handleLinkSubmit = (url: string) => {
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    setShowLinkInput(false);
  };

  return (
    <Toolbar>
      <ToolbarGroup>
        <ToolbarButton
          disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
          title="Отменить"
        >
          <UiKit.icons.Undo />
        </ToolbarButton>
        <ToolbarButton
          disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
          title="Повторить"
        >
          <UiKit.icons.Redo />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarDivider />

      <ToolbarGroup>
        <ToolbarSelect value={getActiveHeading(editor)} onChange={handleHeadingChange}>
          <option value="paragraph">Обычный текст</option>
          <option value="h1">Заголовок 1</option>
          <option value="h2">Заголовок 2</option>
          <option value="h3">Заголовок 3</option>
        </ToolbarSelect>
      </ToolbarGroup>

      <ToolbarGroup>
        <ToolbarSelect value={getActiveFontSize(editor)} onChange={handleFontSizeChange}>
          <option value="">Размер</option>
          {FONT_SIZES.map(size => (
            <option key={size} value={size}>
              {parseInt(size)}
            </option>
          ))}
        </ToolbarSelect>
      </ToolbarGroup>

      <ToolbarDivider />

      <ToolbarGroup>
        <ToolbarButton
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Жирный"
        >
          <UiKit.icons.Bold />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Курсив"
        >
          <UiKit.icons.Italic />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Подчёркнутый"
        >
          <UiKit.icons.Underline />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          title="Зачёркнутый"
        >
          <UiKit.icons.Strikethrough />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarDivider />

      <ToolbarGroup>
        <ToolbarButton
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Маркированный список"
        >
          <UiKit.icons.UnorderedList />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Нумерованный список"
        >
          <UiKit.icons.OrderedList />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarDivider />

      <ToolbarGroup>
        <ToolbarButton
          active={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          title="По левому краю"
        >
          <UiKit.icons.AlignLeft />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          title="По центру"
        >
          <UiKit.icons.AlignCenter />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          title="По правому краю"
        >
          <UiKit.icons.AlignRight />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarDivider />

      <ToolbarGroup>
        <ToolbarButton
          active={editor.isActive('link')}
          onClick={() => setShowLinkInput(!showLinkInput)}
          title="Ссылка"
        >
          <UiKit.icons.Link />
        </ToolbarButton>
        {editor.isActive('link') && (
          <ToolbarButton
            onClick={() => editor.chain().focus().unsetLink().run()}
            title="Убрать ссылку"
          >
            <UiKit.icons.Disconnect />
          </ToolbarButton>
        )}
        <ToolbarButton onClick={insertImage} title="Вставить изображение">
          <UiKit.icons.Picture />
        </ToolbarButton>
      </ToolbarGroup>

      {showLinkInput && (
        <LinkInput onSubmit={handleLinkSubmit} onCancel={() => setShowLinkInput(false)} />
      )}
    </Toolbar>
  );
}

function TextEditor({ value, onChange, className, customStyles }: TextEditorProps) {
  const { validateStatus } = useContext(ValidationContext);

  const editor = useEditor({
    extensions: EXTENSIONS,
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML() as PlainText);
    },
  });

  return (
    <TextEditorWrapper className={className}>
      <EditorToolbar editor={editor} />
      <EditorContainer validateStatus={validateStatus} customStyles={customStyles}>
        <EditorContent editor={editor} />
      </EditorContainer>
    </TextEditorWrapper>
  );
}

const TextEditorWrapper = styled.div``;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 4px 8px;
  border: 1px solid ${colorsPalette.lightGray400};
  border-bottom: none;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  background: ${colorsPalette.lightGray100};
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const ToolbarDivider = styled.div`
  width: 1px;
  height: 20px;
  background: ${colorsPalette.lightGray400};
  margin: 0 4px;
`;

const ToolbarButton = styled.button<{ active?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  border: none;
  border-radius: 4px;
  background: ${p => (p.active ? colorsPalette.lightBlue100 : 'transparent')};
  color: ${p => {
    if (p.disabled) return colorsPalette.gray100;
    if (p.active) return colorsPalette.dustyBlue500;
    return colorsPalette.gray600;
  }};
  cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
  font-size: 14px;
  line-height: 1;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: ${p => (p.active ? colorsPalette.lightBlue200 : colorsPalette.lightGray150)};
  }
`;

const ToolbarSelect = styled.select`
  height: 28px;
  padding: 0 4px;
  border: 1px solid ${colorsPalette.lightGray400};
  border-radius: 4px;
  background: ${colorsPalette.white};
  color: ${colorsPalette.gray600};
  font-size: 13px;
  cursor: pointer;
  outline: none;

  &:hover {
    border-color: ${colorsPalette.dustyBlue500};
  }

  &:focus {
    border-color: ${colorsPalette.dustyBlue500};
    box-shadow: 0 0 0 2px ${colorsPalette.dustyBlue200};
  }
`;

const LinkInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding-top: 4px;
`;

const LinkInputField = styled.input`
  flex: 1;
  height: 28px;
  padding: 0 8px;
  border: 1px solid ${colorsPalette.lightGray400};
  border-radius: 4px;
  background: ${colorsPalette.white};
  color: ${colorsPalette.gray600};
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: ${colorsPalette.dustyBlue500};
    box-shadow: 0 0 0 2px ${colorsPalette.dustyBlue200};
  }
`;

const EditorContainer = styled.div<{
  validateStatus?: ValidateStatus;
  customStyles?: CustomStyles;
}>`
  min-height: ${p => p.customStyles?.minHeight};
  border: 1px solid
    ${p => (p.validateStatus === 'error' ? colorsPalette.red600 : colorsPalette.lightGray400)};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  overflow: hidden;

  .tiptap {
    min-height: 150px;
    padding: 12px 16px;
    outline: none;
    font-size: 14px;
    line-height: 1.6;

    > * + * {
      margin-top: 8px;
    }

    p {
      margin: 0;
    }

    h1 {
      font-size: 2em;
      font-weight: bold;
      margin: 0;
    }

    h2 {
      font-size: 1.5em;
      font-weight: bold;
      margin: 0;
    }

    h3 {
      font-size: 1.17em;
      font-weight: bold;
      margin: 0;
    }

    ul,
    ol {
      margin: 0;
      padding-left: 24px;
    }

    a {
      color: ${colorsPalette.dustyBlue500};
      text-decoration: underline;
    }

    img {
      max-width: 100%;
      height: auto;
      border-radius: 4px;
    }
  }
`;

export { TextEditor };
